import { useCallback, useState } from 'react';
import LoadingPanel from '../../../Abstracts/Components/LoadingPanel';
import useEftposPosLists, { EftposPosListWithItems } from '../../../Hooks/UseEftposPosLists';
import { Link } from 'react-router-dom';
import Alert from '../Alert';
import styled from 'styled-components';
import { BackButton } from '../BreadCrumbs';
import IconLink from '../../../Abstracts/Components/IconLink';
import useEftposAccount from '../../../Hooks/UseEftposAccount';
import useUserApi, { ErrorHandler } from '../../../Services/UserApi';
import { EftposAccount } from 'shared-components';
import LoadingOverlay from '../../../Abstracts/Components/LoadingOverlay';

export default function MenuList() {
	const { eftposAccount, setEftposAccount } = useEftposAccount();
	const [loading, setLoading] = useState(false);
	const eftposPosLists = useEftposPosLists();

	const userApi = useUserApi();
	const setDefaultDepositAccountId = useCallback(
		(depositAccountId: number) => {
			if (!eftposAccount) return;
			const newEftposAccount = {
				...eftposAccount,
				EftposAccountDefaultEftposPosListId: depositAccountId,
			};
			setLoading(true);
			userApi<{ eftposAccount: EftposAccount }>({
				route: 'eftpos/api',
				data: {
					action: 'saveEftposAccount',
					eftposAccount: newEftposAccount,
				},
				errorMessage: 'Error saving eftpos account',
			})
				.then((res) => {
					setEftposAccount(res.data.eftposAccount);
				})
				.catch((e: unknown) => ErrorHandler.toast(e))
				.finally(() => {
					setLoading(false);
				});
		},
		[userApi, eftposAccount, setEftposAccount],
	);

	if (!eftposPosLists || !eftposAccount) {
		return <LoadingPanel />;
	}

	return (
		<>
			{loading && <LoadingOverlay text={'Saving'} />}
			<BackButton to="/" />
			<MenuWarning posLists={eftposPosLists} context="menus" />
			{eftposPosLists.length !== 0 && (
				<>
					<Table className="table table-striped">
						<thead>
							<tr>
								<th className="desktop">Default</th>
								<th>Menu</th>
								<th className="desktop">Items</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{eftposPosLists.map((posList) => (
								<tr key={posList.EftposPosListId}>
									<td className="desktop is-default">
										<div>
											<input
												type="radio"
												checked={
													eftposAccount.EftposAccountDefaultEftposPosListId ===
													posList.EftposPosListId
												}
												onChange={() => setDefaultDepositAccountId(posList.EftposPosListId)}
											/>
										</div>
									</td>
									<td>{posList.EftposPosListName}</td>
									<td className="desktop">{posList.items.length}</td>
									<td className="text-right">
										<IconLink
											to={`/menus/edit/${posList.EftposPosListId}`}
											className="btn btn-sm btn-blue"
											iconClass="fa fa-edit"
										>
											Edit menu
										</IconLink>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
					<NewMenuBtn className="btn btn-blue" to="/menus/create">
						<i className="fa fa-plus" />
						<span>Create a new menu</span>
					</NewMenuBtn>
				</>
			)}
		</>
	);
}

const Table = styled.table`
	.is-default div {
		margin-left: 16px;
	}

	th.desktop,
	td.desktop {
		@media (max-width: 768px) {
			display: none;
		}
	}
`;

export const MenuWarning = (props: { posLists?: EftposPosListWithItems[]; context: string }) => {
	const { context } = props;
	if (!props.posLists || props.posLists.length !== 0) {
		return null;
	}

	return (
		<Alert className="alert alert-warning">
			<i className="fa fa-exclamation-triangle fa-lg"></i>
			<span>You have no menu!</span>
			<Link to={`/menus/create?context=${context}`} className="ml-auto btn btn-sm btn-blue">
				Create a menu
			</Link>
		</Alert>
	);
};

const NewMenuBtn = styled(Link)`
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: flex-end;

	i {
		margin-right: 1rem;
	}
`;
